import React, { useState } from 'react';
import { Button, Modal, Spin } from 'antd';
import { toast } from 'react-hot-toast';
import { IoCloudDownloadOutline } from 'react-icons/io5';
import { exportToCSV, generateCSVFilename, formatDateForCSV } from '@/utils/csv';
import { formatDealerAmount } from '@/utils/currency';
import { PageHeader } from '@/components/common/PageHeader';
import {
  CreditControlStats,
  CreditWarningBanner,
  CreditOrdersTable,
} from '../components';
import { IDataverseOrder } from '@/features/orders-management/types';
import { useGetCurrentUser } from '@/hooks/app';
import { PermissionChecker } from '@/utils/permission';
import { useCreditControlOrders, useApproveCreditLimit } from '../hooks';
import { IOrderQueryParams } from '@/features/orders-management/types';
import { REGION_ALL } from '@/utils/constant/appConstant';
import { generateRegionFilterQuery, combineFilterQueries, getOrderNumber } from '@/features/orders-management/utils';
import { CREDIT_CONTROL_STATUS_CODE } from '../constants';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_STARTING_PAGE = 1;

export const CreditControlPageAntd: React.FC = () => {
  // Get current user permissions
  const { data: currentUserData } = useGetCurrentUser();
  const permissions = currentUserData?.permission;

  // Initialize PermissionChecker
  const permissionChecker = new PermissionChecker(permissions);

  // Permission checks
  const canApproveCredit = permissionChecker.canApproveCredit();

  // Query params for API
  const [queryParams, setQueryParams] = useState<IOrderQueryParams>({
    current: DEFAULT_STARTING_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
    filter: '',
  });

  // Region filter
  const userRegion = currentUserData?.currentUser?.dogma_region;
  const isRegionAll = Boolean(userRegion && userRegion === REGION_ALL);
  const regionFilterQuery = isRegionAll ? "" : generateRegionFilterQuery(userRegion);

  // Statuscode filter for credit control (736000002)
  const statuscodeFilterQuery = `statuscode eq ${CREDIT_CONTROL_STATUS_CODE}`;

  // Combine filters
  const combinedFilters = combineFilterQueries([
    statuscodeFilterQuery,
    regionFilterQuery
  ]);

  // Fetch credit control orders from API
  const { data: orderListResponse, isLoading } = useCreditControlOrders({
    queryParams: {
      ...queryParams,
      filter: combinedFilters ? `&$filter=${combinedFilters}` : ''
    },
    enabled: Boolean(currentUserData?.currentUser?.systemuserid)
  });

  // Extract orders from API response
  const orders = orderListResponse?.value || [];

  // Mutation for approving/rejecting credit limits
  const approveCreditLimitMutation = useApproveCreditLimit();

  // All orders from API are pending credit approval (filtered by statuscode 736000002)
  const pendingOrders = orders;

  // TODO: Track approved/rejected orders separately if needed
  const approvedOrders: IDataverseOrder[] = [];
  const rejectedOrders: IDataverseOrder[] = [];

  const handleTableChange = (current: number, pageSize: number) => {
    setQueryParams((prev) => ({ ...prev, current, pageSize }));
  };

  const handleExportCSV = () => {
    const csvData = orders.map((o) => {
      const dealersCurrency = o.quoteid?.dogma_DealersCurrency;
      const exchangeRate = o.quoteid?.dogma_exchangerate ?? dealersCurrency?.dogma_exchangerate ?? 1;
      return {
        'Order Number': getOrderNumber(o) || '-',
        'Customer': o['_customerid_value@OData.Community.Display.V1.FormattedValue'] || '-',
        'Order Status': o['statuscode@OData.Community.Display.V1.FormattedValue'] || '-',
        'Payment Status': o['dogma_paymentstatus@OData.Community.Display.V1.FormattedValue'] || '-',
        'Date Ordered': o.createdon ? formatDateForCSV(o.createdon) : '-',
        'Total Amount': o.totalamount != null ? formatDealerAmount(o.totalamount, dealersCurrency, exchangeRate) : '-',
      };
    });
    const columns = [
      { key: 'Order Number' as const, header: 'Order Number' },
      { key: 'Customer' as const, header: 'Customer' },
      { key: 'Order Status' as const, header: 'Order Status' },
      { key: 'Payment Status' as const, header: 'Payment Status' },
      { key: 'Date Ordered' as const, header: 'Date Ordered' },
      { key: 'Total Amount' as const, header: 'Total Amount' },
    ];
    exportToCSV(csvData, columns, generateCSVFilename('credit-control'));
  };

  // Handle approval
  const handleApprove = (order: IDataverseOrder) => {
    Modal.confirm({
      title: 'Approve Order',
      content: `Approve order ${getOrderNumber(order)}?`,
      okText: 'Approve',
      okButtonProps: { className: 'bg-green-600 hover:bg-green-700' },
      onOk: async () => {
        try {
          await approveCreditLimitMutation.mutateAsync({
            orderId: order.salesorderid,
            status: "approve",
          });

          Modal.success({
            title: 'Order Approved',
            content: `Order ${getOrderNumber(order)} approved and released for processing.`,
          });
        } catch (error) {
          toast.error('Failed to approve the order. Please try again.');
        }
      },
    });
  };

  // Handle rejection
  const handleReject = (order: IDataverseOrder) => {
    Modal.confirm({
      title: 'Reject Order',
      content: `Are you sure you want to reject order ${getOrderNumber(order)}?`,
      okText: 'Reject',
      okType: 'danger',
      onOk: async () => {
        try {
          await approveCreditLimitMutation.mutateAsync({
            orderId: order.salesorderid,
            status: "reject",
            newCreditLimit: 0,
          });

          Modal.success({
            title: 'Order Rejected',
            content: `Order ${getOrderNumber(order)} rejected. Sales team will be notified.`,
          });
        } catch (error) {
          toast.error('Failed to reject the order. Please try again.');
        }
      },
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" tip="Loading credit control orders..." />
      </div>
    );
  }

  return (
    <>
      {/* Page Header */}
      <div className="px-4 pt-4">
        <PageHeader
          title="Credit Control"
          subtitle="Review and approve orders requiring credit checks"
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: 'Credit Control' },
          ]}
          actions={
            <Button
              icon={<IoCloudDownloadOutline />}
              onClick={handleExportCSV}
            >
              Export CSV
            </Button>
          }
        />
      </div>

      {/* Warning Banner */}
      <div className="px-4 pt-4">
        <CreditWarningBanner />
      </div>

      {/* Stats Cards */}
      <div className="px-4">
        <CreditControlStats
          pendingCount={pendingOrders.length}
          approvedCount={approvedOrders.length}
          rejectedCount={rejectedOrders.length}
        />
      </div>

      {/* Orders Table */}
      <div className="p-4">
        <CreditOrdersTable
          orders={pendingOrders}
          onApprove={handleApprove}
          onReject={handleReject}
          canApproveCredit={canApproveCredit}
          loading={isLoading}
          current={queryParams.current}
          pageSize={queryParams.pageSize}
          total={orderListResponse?.['@odata.count']}
          nextLink={orderListResponse?.['@odata.nextLink']}
          totalRecordCountLimitExceeded={orderListResponse?.['@Microsoft.Dynamics.CRM.totalrecordcountlimitexceeded']}
          onPageChange={handleTableChange}
        />
      </div>
    </>
  );
};

export default CreditControlPageAntd;
